import { Capacitor } from "@capacitor/core";
import { StatsigProvider, useClientAsyncInit } from "@statsig/react-bindings";
import { StatsigSessionReplayPlugin } from "@statsig/session-replay";
import { StatsigAutoCapturePlugin } from "@statsig/web-analytics";
import type React from "react";
import { useMemo } from "react";

// Development mode detection
const isDevelopment = import.meta.env.DEV;

// Log once on module load in development
if (isDevelopment) {
	console.log(
		"[Statsig] Development mode - events logged to console only, no data sent to server",
	);
}

interface EraStatsigProviderProps {
	children: React.ReactNode;
	userId: string;
	userEmail?: string;
	userName?: string;
	userPicture?: string;
}

export function EraStatsigProvider({
	children,
	userId,
	userEmail,
	userName,
	userPicture,
}: EraStatsigProviderProps) {
	// Get platform information from Capacitor (stable values)
	const platform = Capacitor.getPlatform(); // 'ios', 'android', or 'web'
	const isNative = Capacitor.isNativePlatform();

	// Memoize Statsig user object to prevent unnecessary reinitializations
	const statsigUser = useMemo(
		() => ({
			userID: userId,
			email: userEmail || undefined,
			custom: {
				auth0Id: userId,
				name: userName || undefined,
				picture: userPicture || undefined,
				platform: platform,
				isNative: String(isNative),
				appVersion: "0.0.0",
			},
		}),
		[userId, userEmail, userName, userPicture, platform, isNative],
	);

	// Memoize plugins to prevent recreating them on every render
	const plugins = useMemo(
		() => [new StatsigAutoCapturePlugin(), new StatsigSessionReplayPlugin()],
		[],
	);

	// Initialize Statsig SDK with plugins (must be called unconditionally)
	// In development, disable logging to prevent sending data to Statsig servers
	const { client } = useClientAsyncInit(
		import.meta.env.VITE_STATSIG_CLIENT_KEY || "client-dev-key",
		statsigUser,
		{
			plugins: isDevelopment ? [] : plugins, // Disable plugins in dev (session replay, autocapture)
			disableLogging: isDevelopment, // Prevent sending events in development
		},
	);

	// Only skip Statsig when no client key is provided
	if (!import.meta.env.VITE_STATSIG_CLIENT_KEY) {
		return <>{children}</>;
	}

	return (
		<StatsigProvider client={client} loadingComponent={<div>Loading...</div>}>
			{children}
		</StatsigProvider>
	);
}
